import { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Instagram, Facebook, Linkedin, Twitter, Lock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Logo } from "./Logo";
import { SITE, scrollToId } from "../lib/site";

const POLICIES = {
  privacy: {
    title: "Privacy Policy",
    body: "Ad Neurone Digital collects only the details you share through our forms — name, email, phone number, business name and message — solely to contact you about your enquiry and prepare your strategy call. We never sell, rent or share your information with third parties. Analytics and advertising pixels (Meta, Google) may be used to measure ad performance; you can opt out via your browser or device settings. To have your data corrected or deleted, email us at support@adneuronedigital.com.",
  },
  terms: {
    title: "Terms of Service",
    body: "By using this website you agree that the information provided is for general guidance and does not constitute a guaranteed outcome. Marketing performance depends on budget, market, offer and sales follow-up. Strategy calls are free and carry no obligation. Service engagements are governed by individual agreements signed between Ad Neurone Digital and the client. All content on this site is the property of Ad Neurone Digital.",
  },
};

const SOCIALS = [
  { icon: Instagram, label: "Instagram", href: "https://instagram.com" },
  { icon: Facebook, label: "Facebook", href: "https://facebook.com" },
  { icon: Linkedin, label: "LinkedIn", href: "https://linkedin.com" },
  { icon: Twitter, label: "X (Twitter)", href: "https://x.com" },
];

export const Footer = () => {
  const [openPolicy, setOpenPolicy] = useState(null);
  const policy = openPolicy ? POLICIES[openPolicy] : null;

  return (
    <footer data-testid="site-footer" className="border-t border-white/10 bg-[#04081A] px-4 sm:px-8 lg:px-12 pt-14 pb-8">
      <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
        <div className="space-y-4">
          <Logo />
          <p className="text-sm text-slate-400 leading-relaxed max-w-xs">{SITE.tagline}</p>
          <div className="flex gap-2.5">
            {SOCIALS.map((s) => (
              <a
                key={s.label}
                data-testid={`social-${s.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:border-cyan-400/50 transition-colors duration-200"
              >
                <s.icon size={15} />
              </a>
            ))}
          </div>
        </div>
        <div>
          <h4 className="font-display font-semibold text-white text-sm mb-4 uppercase tracking-wider">Services</h4>
          <ul className="space-y-2.5 text-sm text-slate-400">
            {["Performance Marketing", "Lead Generation", "Social Media Handling", "Sales Funnel & Growth"].map((s) => (
              <li key={s}>
                <button onClick={() => scrollToId("services")} className="hover:text-cyan-300 transition-colors duration-200">
                  {s}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="font-display font-semibold text-white text-sm mb-4 uppercase tracking-wider">Company</h4>
          <ul className="space-y-2.5 text-sm text-slate-400">
            <li>
              <button data-testid="privacy-policy-link" onClick={() => setOpenPolicy("privacy")} className="hover:text-cyan-300 transition-colors duration-200">
                Privacy Policy
              </button>
            </li>
            <li>
              <button data-testid="terms-link" onClick={() => setOpenPolicy("terms")} className="hover:text-cyan-300 transition-colors duration-200">
                Terms of Service
              </button>
            </li>
            <li>
              <Link data-testid="admin-login-link" to="/admin" className="inline-flex items-center gap-1.5 hover:text-cyan-300 transition-colors duration-200">
                <Lock size={12} /> Admin Login
              </Link>
            </li>
          </ul>
        </div>
        <div>
          <h4 className="font-display font-semibold text-white text-sm mb-4 uppercase tracking-wider">Contact</h4>
          <ul className="space-y-3 text-sm text-slate-400">
            <li>
              <a data-testid="footer-phone" href={`tel:${SITE.phoneRaw}`} className="flex items-center gap-2.5 hover:text-cyan-300 transition-colors duration-200">
                <Phone size={14} className="text-cyan-400 shrink-0" /> {SITE.phone}
              </a>
            </li>
            <li>
              <a data-testid="footer-email" href={`mailto:${SITE.email}`} className="flex items-center gap-2.5 hover:text-cyan-300 transition-colors duration-200">
                <Mail size={14} className="text-cyan-400 shrink-0" /> {SITE.email}
              </a>
            </li>
            <li className="flex items-center gap-2.5">
              <MapPin size={14} className="text-cyan-400 shrink-0" /> {SITE.city}
            </li>
          </ul>
        </div>
      </div>
      <div className="max-w-7xl mx-auto mt-12 pt-6 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="text-xs text-slate-500">© {new Date().getFullYear()} Ad Neurone Digital. All rights reserved.</p>
        <p className="text-xs text-slate-600 font-mono2">Built for growth. Powered by data.</p>
      </div>

      <Dialog open={!!openPolicy} onOpenChange={(o) => !o && setOpenPolicy(null)}>
        <DialogContent data-testid="policy-dialog" className="bg-[#0B132B] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-white">{policy?.title}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-300 leading-relaxed">{policy?.body}</p>
        </DialogContent>
      </Dialog>
    </footer>
  );
};
